import { Check, ChevronDown } from "lucide-react";
import { Select as SelectPrimitive } from "radix-ui";
import {
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import { appConfig } from "@/config/app";
import { cx } from "@/lib/cx";

import { Label, describedBy, inputClass, useFieldIds } from "./Field";
import fieldStyles from "./Field.module.css";
import floating from "./Floating.module.css";
import styles from "./Select.module.css";
import { nextMatch, TYPEAHEAD_RESET_MS } from "./selectTypeahead";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** The titled group it is listed under; consecutive options with one group share its title. */
  group?: string | undefined;
  /** Secondary text after the label in the list (a file name); the trigger shows the label only. */
  description?: string | undefined;
  /** The label's `font-family` in the list, for a choice of fonts shown in their own face. */
  fontFamily?: string | undefined;
  /** A colour swatch before the label, in the list and the trigger (a layer's or an object's colour), as CSS. */
  swatch?: string | undefined;
  /** A drawing before the label, in the list and the trigger (a linetype's pattern). */
  preview?: ReactNode;
  /** A rule above the option, setting it apart from the ones before (a list's "Others…"). */
  separated?: boolean | undefined;
  /** A way out of the list rather than a value ("Add fonts…"): set apart at the foot and in the accent colour. */
  action?: boolean | undefined;
}

/** Consecutive options under one group title (`undefined`: no title). */
function grouped(options: readonly SelectOption[]): { title?: string; options: SelectOption[] }[] {
  const out: { title?: string; options: SelectOption[] }[] = [];
  for (const option of options) {
    const last = out.at(-1);
    if (last !== undefined && last.title === option.group) last.options.push(option);
    else
      out.push({
        ...(option.group === undefined ? {} : { title: option.group }),
        options: [option],
      });
  }
  return out;
}

/** `ticked`: the option's own tick, apart from the value (undefined: the value's option is ticked). */
function Item({ option, ticked }: { option: SelectOption; ticked?: boolean | undefined }) {
  const face: CSSProperties | undefined =
    option.fontFamily === undefined ? undefined : { fontFamily: option.fontFamily };
  return (
    <>
      {option.separated === true && <SelectPrimitive.Separator className={styles.separator} />}
      <SelectPrimitive.Item
        value={option.value}
        data-select-value={option.value}
        {...(option.disabled === undefined ? {} : { disabled: option.disabled })}
        className={cx(floating.item, floating.indented, option.action === true && styles.action)}
      >
        {ticked === undefined ? (
          <SelectPrimitive.ItemIndicator className={floating.indicator}>
            <Check aria-hidden="true" />
          </SelectPrimitive.ItemIndicator>
        ) : (
          ticked && (
            <span className={floating.indicator} data-ticked="">
              <Check aria-hidden="true" />
            </span>
          )
        )}
        <span style={face}>
          <SelectPrimitive.ItemText>
            {option.preview !== undefined && (
              <span aria-hidden="true" data-select-preview="" className={styles.preview}>
                {option.preview}
              </span>
            )}
            {option.swatch !== undefined && (
              <span
                aria-hidden="true"
                className={styles.swatch}
                style={{ background: option.swatch }}
              />
            )}
            {option.label}
          </SelectPrimitive.ItemText>
        </span>
        {option.description !== undefined && (
          <span className={styles.description}>{option.description}</span>
        )}
      </SelectPrimitive.Item>
    </>
  );
}

export interface SelectProps extends Omit<
  ComponentProps<typeof SelectPrimitive.Root>,
  "children" | "dir"
> {
  label: string;
  options: readonly SelectOption[];
  placeholder?: string;
  description?: ReactNode;
  hideLabel?: boolean;
  id?: string;
  className?: string | undefined;
  /** The closed control carries the chosen option's name as its tooltip, for a box too narrow to show it whole. */
  titled?: boolean;
  /** Quiet help text at the foot of the open list (not a choice). */
  footer?: ReactNode;
  /**
   * The option the list ticks, when it is not the value: the current layer, while the control shows the selected
   * objects' layer. Undefined: the value's option is ticked.
   */
  ticked?: string | undefined;
}

/** The option an element in the open list stands for, if any. */
function itemValue(target: EventTarget): string | undefined {
  if (!(target instanceof Element)) return undefined;
  return target.closest("[data-select-value]")?.getAttribute("data-select-value") ?? undefined;
}

/**
 * One choice from a fixed list, in titled groups when its options name them. Typing in the open list moves to the
 * option the letters start, and Enter picks that option however soon after the letters it comes (`selectTypeahead`).
 */
export function Select({
  label,
  options,
  placeholder,
  description,
  hideLabel = false,
  id,
  className,
  footer,
  ticked,
  titled = false,
  open: openProp,
  defaultOpen,
  onOpenChange,
  value: valueProp,
  defaultValue,
  onValueChange,
  ...rootProps
}: SelectProps) {
  const ids = useFieldIds(id);
  const [ownOpen, setOwnOpen] = useState(defaultOpen ?? false);
  const [ownValue, setOwnValue] = useState(defaultValue);
  const typed = useRef<{ search: string; match?: string | undefined; timer?: number | undefined }>({
    search: "",
  });
  const forget = () => {
    window.clearTimeout(typed.current.timer);
    typed.current = { search: "" };
  };
  const tickOf = (option: SelectOption) =>
    ticked === undefined ? undefined : option.value === ticked;
  const changeOpen = (next: boolean) => {
    forget();
    setOwnOpen(next);
    onOpenChange?.(next);
  };
  const changeValue = (next: string) => {
    setOwnValue(next);
    onValueChange?.(next);
  };
  // Before Radix's own handlers: the typed prefix's match is kept here, and Enter takes it rather than the item that
  // still has the focus while Radix's timer has yet to move it.
  const onListKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.ctrlKey || event.altKey || event.metaKey) return;
    const now = typed.current;
    if (event.key.length === 1 && (event.key !== " " || now.search !== "")) {
      const search = now.search + event.key;
      const match = nextMatch(options, search, now.match ?? itemValue(event.target));
      window.clearTimeout(now.timer);
      typed.current = {
        search,
        match: match ?? now.match,
        timer: window.setTimeout(forget, TYPEAHEAD_RESET_MS),
      };
      return;
    }
    if (event.key === "Enter" && now.match !== undefined) {
      event.preventDefault();
      event.stopPropagation();
      if (now.match !== (valueProp ?? ownValue)) changeValue(now.match);
      changeOpen(false);
      return;
    }
    if (event.key !== "Shift") forget();
  };
  const chosen = options.find((option) => option.value === (valueProp ?? ownValue))?.label;
  return (
    <div className={cx(fieldStyles.field, className)}>
      <Label htmlFor={ids.controlId} hidden={hideLabel}>
        {label}
      </Label>
      <SelectPrimitive.Root
        {...rootProps}
        open={openProp ?? ownOpen}
        onOpenChange={changeOpen}
        {...((valueProp ?? ownValue) === undefined ? {} : { value: valueProp ?? ownValue })}
        onValueChange={changeValue}
      >
        <SelectPrimitive.Trigger
          id={ids.controlId}
          aria-describedby={describedBy(ids, description !== undefined, false)}
          className={cx(inputClass, styles.trigger)}
          {...(titled && chosen !== undefined ? { title: chosen } : {})}
        >
          <SelectPrimitive.Value {...(placeholder === undefined ? {} : { placeholder })} />
          <SelectPrimitive.Icon className={styles.chevron}>
            <ChevronDown aria-hidden="true" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            position="popper"
            sideOffset={appConfig.overlayOffsetPx}
            className={cx(floating.panel, styles.content, "animate-in")}
            onKeyDownCapture={onListKey}
          >
            <SelectPrimitive.Viewport className={floating.list}>
              {grouped(options).map((group, index) =>
                group.title === undefined ? (
                  group.options.map((option) => (
                    <Item key={option.value} option={option} ticked={tickOf(option)} />
                  ))
                ) : (
                  <SelectPrimitive.Group key={`${group.title}-${String(index)}`}>
                    <SelectPrimitive.Label className={styles.groupLabel}>
                      {group.title}
                    </SelectPrimitive.Label>
                    {group.options.map((option) => (
                      <Item key={option.value} option={option} ticked={tickOf(option)} />
                    ))}
                  </SelectPrimitive.Group>
                ),
              )}
            </SelectPrimitive.Viewport>
            {footer !== undefined && <p className={styles.footer}>{footer}</p>}
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
      {description !== undefined && (
        <p id={ids.descriptionId} className={fieldStyles.description}>
          {description}
        </p>
      )}
    </div>
  );
}
